"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import type { FilterConfig } from "@/lib/types/data.types";
import type { FilterColumnDef } from "./types";
import { getColumnDef, getOperatorsForColumn } from "./helpers";
import { FilterValueInput } from "./FilterValueInput";
import { Select } from "@/components/forms";

interface FilterFormProps {
  columns: FilterColumnDef[];
  /** When set, the form is in edit mode. */
  editingFilter?: { index: number; filter: FilterConfig } | null;
  onAdd: (filter: FilterConfig) => void;
  onUpdate: (index: number, filter: FilterConfig) => void;
  onCancelEdit?: () => void;
}

export function FilterForm({
  columns,
  editingFilter,
  onAdd,
  onUpdate,
  onCancelEdit,
}: FilterFormProps) {
  const [selectedColumn, setSelectedColumn] = useState("");
  const [selectedOperator, setSelectedOperator] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [selectedMultiValues, setSelectedMultiValues] = useState<string[]>([]);

  const columnDef = useMemo(
    () => getColumnDef(columns, selectedColumn),
    [columns, selectedColumn]
  );

  const operators = useMemo(
    () => (columnDef ? getOperatorsForColumn(columnDef) : []),
    [columnDef]
  );

  const isEditing = editingFilter !== null && editingFilter !== undefined;
  const isMultiSelect = columnDef?.type === "multi-select";

  // Populate form when entering edit mode
  useEffect(() => {
    if (!editingFilter) return;
    const { filter } = editingFilter;
    setSelectedColumn(filter.column);
    setSelectedOperator(filter.operator);
    if (
      getColumnDef(columns, filter.column)?.type === "multi-select"
    ) {
      const parts = String(filter.value)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      setSelectedMultiValues(parts);
      setFilterValue("");
    } else {
      setFilterValue(String(filter.value));
      setSelectedMultiValues([]);
    }
  }, [editingFilter, columns]);

  // Reset operator/value when column changes (not during edit population)
  const handleColumnChange = useCallback(
    (key: string) => {
      setSelectedColumn(key);
      const col = columns.find((c) => c.key === key);
      const ops = col ? getOperatorsForColumn(col) : [];
      setSelectedOperator(ops.length === 1 ? ops[0].value : "");
      setFilterValue("");
      setSelectedMultiValues([]);
    },
    [columns]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedColumn || !selectedOperator) return;

    const value = isMultiSelect
      ? selectedMultiValues.join(",")
      : filterValue;

    if (!value) return;

    const filter: FilterConfig = {
      column: selectedColumn,
      operator: selectedOperator,
      value,
    };

    if (isEditing && editingFilter) {
      onUpdate(editingFilter.index, filter);
    } else {
      onAdd(filter);
    }

    // Reset form
    setSelectedColumn("");
    setSelectedOperator("");
    setFilterValue("");
    setSelectedMultiValues([]);
  }, [
    selectedColumn,
    selectedOperator,
    filterValue,
    selectedMultiValues,
    isMultiSelect,
    isEditing,
    editingFilter,
    onAdd,
    onUpdate,
  ]);

  const handleCancel = useCallback(() => {
    setSelectedColumn("");
    setSelectedOperator("");
    setFilterValue("");
    setSelectedMultiValues([]);
    onCancelEdit?.();
  }, [onCancelEdit]);

  const canSubmit =
    selectedColumn &&
    selectedOperator &&
    (isMultiSelect ? selectedMultiValues.length > 0 : !!filterValue);

  const hideOperatorSelect =
    columnDef && operators.length === 1;

  return (
    <div className="space-y-3">
      {/* Column */}
      <Select
        label="Column"
        placeholder="Select column"
        value={selectedColumn}
        onChange={(value) => handleColumnChange(value as string)}
        options={columns.map((col) => ({ value: col.key, label: col.label }))}
      />

      {/* Operator (hidden when column has single operator) */}
      {selectedColumn && !hideOperatorSelect && (
        <Select
          label="Operator"
          placeholder="Select operator"
          value={selectedOperator}
          onChange={(value) => setSelectedOperator(value as string)}
          options={operators}
        />
      )}

      {/* Value */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isMultiSelect ? "Values" : "Value"}
        </label>
        {columnDef ? (
          <FilterValueInput
            columnDef={columnDef}
            value={filterValue}
            onChange={setFilterValue}
            selectedValues={selectedMultiValues}
            onSelectedValuesChange={setSelectedMultiValues}
            onSubmit={canSubmit ? handleSubmit : undefined}
          />
        ) : (
          <input
            type="text"
            disabled
            value=""
            placeholder="Select a column first"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400"
          />
        )}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        {isEditing && (
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="flex-1 py-2 text-sm font-medium rounded-lg transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed bg-gray-800 text-white hover:bg-gray-700"
        >
          {isEditing ? "Update Filter" : "Add Filter"}
        </button>
      </div>
    </div>
  );
}
